<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Button from '../components/Button.svelte';
  import Alert from '../components/Alert.svelte';
  import { createDataset, addSource, getSourceDraft, runIngest, getDatasets, getVocabularies } from '../lib/api.js';
  import { parseAndProfileCsv } from '../../../lib/disclosure_core.js';
  import { guessColumns, withKind, bands, columnProblems, periodProblem, datasetIdFrom, unitFrom,
    namesFor, qualifierOf, TERM_KINDS, LISTED_KINDS } from '../../../lib/dataset_from_file.js';
  import { matchColumns, valueMatch } from '../../../lib/vocabulary_match.js';
  import { schemaLabel } from '../../../lib/schema_label.js';
  import { session } from '../lib/session.svelte.js';
  import { SELF } from '../../shared/config.js';
  import { siblingPeriod } from '../../../lib/source_setup.js';
  import { NEW_DATASET_RULES } from '../../../lib/datasets.js';
  import { go, nav } from '../lib/nav.svelte.js';

  // A new dataset from one file (docs/ref-belonging-plan.md, step 4): each
  // column becomes a term named after its header, with a guessed kind the
  // steward corrects. Or, described with a vocabulary someone publishes
  // (step 5.5), each column is matched to one of its terms by name and reuses
  // it; a column without one still becomes a term of our own.
  //
  // Or the file is another source for a dataset there is (step 7.4): it is
  // described with that dataset's terms, matched by name the same way, and a
  // column with no match is left out unless the steward makes it a new term.
  const prefix = session.context?.self?.institution ?? SELF.institution;
  const KIND_LABELS = { scale: 'Scale', category: 'Category', list: 'List', number: 'Number', text: 'Free text',
    date: 'Date', key: 'Key', skip: 'Leave out' };

  // 'new' makes a dataset; 'add' adds the file to `target` as a source.
  let mode = $state(nav.params.to || nav.params.add === '1' ? 'add' : 'new');
  let target = $state(nav.params.to ?? '');
  let draft = $state(null); // getSourceDraft(target): its record classes' terms, its sources' periods
  let recordClassId = $state('');
  $effect(() => {
    if (mode !== 'add' || !target) { draft = null; return; }
    const asked = target;
    getSourceDraft(asked).then((d) => {
      if (asked !== target) return;
      draft = d; recordClassId = d.recordClasses[0]?.id ?? '';
      matchToDataset();
    }).catch((e) => { error = e.message; });
  });
  const adding = $derived(mode === 'add');

  let label = $state('');
  let id = $state('');
  let idEdited = $state(false);
  let singular = $state('record');
  const datasetId = $derived(idEdited ? id : datasetIdFrom(label));

  let file = $state(null); // { fileName, mediaType, csvText, parsed }
  let columns = $state([]);
  let sourceKey = $state('@row');
  let timestampColumn = $state('');
  let period = $state({ kind: 'quarter', value: 'T0', column: '' });
  let error = $state(null);
  let busy = $state(null);

  // "Describe it with": our own terms, or a vocabulary we hold or a partner
  // publishes. `choice` is the request's { from, prefix, webId }.
  let vocabularies = $state({ held: [], partners: [] });
  getVocabularies().then((v) => { vocabularies = v; }).catch(() => {});
  let choiceKey = $state('');
  const choices = $derived([
    ...vocabularies.held.map((v) => ({ key: `held:${v.prefix}`, choice: { from: 'held', prefix: v.prefix }, vocabulary: v,
      label: `${schemaLabel(v.schema) ?? v.prefix}${v.own ? ' (ours)' : ' (already used here)'}` })),
    ...vocabularies.partners.flatMap((p) => p.vocabularies.map((v) => ({ key: `partner:${p.webId}:${v.prefix}`,
      choice: { from: 'partner', webId: p.webId, prefix: v.prefix }, vocabulary: v,
      label: `${schemaLabel(v.schema) ?? v.prefix} (from ${p.label})` }))),
  ]);
  const addVocabulary = $derived(draft?.recordClasses.find((r) => r.id === recordClassId)?.vocabulary ?? null);
  const chosen = $derived(adding ? (addVocabulary ? { vocabulary: addVocabulary, choice: null } : null)
    : choices.find((c) => c.key === choiceKey) ?? null);
  const chosenName = $derived(adding ? draft?.dataset.label ?? '' : chosen ? schemaLabel(chosen.vocabulary.schema) ?? chosen.vocabulary.prefix : '');
  const termOf = (id) => chosen?.vocabulary.terms.find((t) => t.id === id) ?? null;
  const termText = (t) => t.label ?? t.means ?? t.id;
  function describeWith(key) {
    choiceKey = key;
    const vocabulary = choices.find((c) => c.key === key)?.vocabulary;
    const matches = vocabulary ? matchColumns(columns, vocabulary) : columns.map(() => null);
    columns = columns.map((c, i) => ({ ...c, use: matches[i] }));
  }
  // Matched to the dataset's terms; a column with no match is left out until
  // the steward gives it a value type, which makes it a new term.
  function matchToDataset() {
    if (!adding || !addVocabulary || !file) return;
    const matches = matchColumns(columns, addVocabulary);
    columns = columns.map((c, i) => ({ ...c, use: matches[i],
      ...(matches[i] || !TERM_KINDS.has(c.kind) ? {} : withKind(c, 'skip')) }));
    const sibling = siblingPeriod([{ columns: draft.periods }], file.parsed.headers);
    if (sibling?.transform?.fn === 'const') period = { kind: 'fixed', value: sibling.transform.value, column: '' };
    else if (sibling?.transform?.fn === 'dateBucket') {
      timestampColumn = sibling.source;
      period = { kind: sibling.transform.granularity === 'year' ? 'year' : 'quarter', value: 'T0', column: '' };
    } else if (sibling) period = { kind: 'column', value: 'T0', column: sibling.source };
  }

  // Switching between a new dataset and adding a source starts the columns over.
  function remake() {
    if (!file) return;
    columns = guessColumns(file.parsed).map((c) => ({ ...c, checked: false, added: '', use: null }));
    if (adding) matchToDataset(); else if (choiceKey) describeWith(choiceKey);
  }

  const reuseValues = (column) => {
    const term = termOf(column.use);
    return term ? valueMatch(Object.keys(column.raw), term, chosen.vocabulary) : null;
  };
  const conceptLabel = (scheme, id) => scheme?.concepts?.find((c) => String(c.id) === id)?.label ?? id;

  // Values seen fewer times than a cube shares are flagged: a rare value can
  // point to a person. The rules are the dataset's: a new one's, or those of
  // the dataset the file is added to.
  const rules = $derived(adding && draft ? draft.dataset : NEW_DATASET_RULES);
  const rareBelow = $derived(rules.minPerColumn ?? rules.suppressBelow);

  async function choose(event) {
    error = null;
    const chosen = event.currentTarget.files?.[0];
    if (!chosen) return;
    try {
      if (chosen.size > 5 * 1024 * 1024) throw new Error('CSV exceeds the 5 MB limit.');
      const csvText = await chosen.text();
      const parsed = parseAndProfileCsv(csvText);
      file = { fileName: chosen.name, mediaType: chosen.type || 'text/csv', csvText, parsed };
      columns = guessColumns(parsed).map((c) => ({ ...c, checked: false, added: '', use: null }));
      if (choiceKey && !adding) describeWith(choiceKey);
      sourceKey = columns.find((c) => c.kind === 'key')?.name ?? '@row';
      timestampColumn = columns.find((c) => c.kind === 'date')?.name ?? '';
      if (!timestampColumn) period.kind = 'fixed';
      if (!label) label = chosen.name.replace(/\.csv$/i, '').replace(/[_-]+/g, ' ');
      matchToDataset();
    } catch (e) { file = null; columns = []; error = e.message; }
  }

  // Changing the type, or a list's separator, re-reads the column's values.
  const setKind = (i, kind, extra = {}) => { columns[i] = { ...withKind({ ...columns[i], ...extra }, kind), added: '' }; };
  const setEdges = (column, text) => {
    column.edges = text.split(/[,\s]+/).filter(Boolean).map(Number);
  };
  function addValue(column) {
    const value = column.added.trim();
    if (value && !column.values.includes(value)) column.values = [...column.values, value];
    column.added = ''; column.checked = false;
  }

  // The names already used by a dataset: when this one's plain names clash,
  // the server qualifies them all by its short name (buildDataset).
  let taken = $state(new Set());
  let holder = $state(new Map()); // record class → the dataset that holds it
  let datasets = $state([]);
  getDatasets().then(({ datasets: all }) => {
    datasets = all;
    holder = new Map(all.flatMap((d) => d.recordClasses.map((c) => [c.id, d.label])));
    taken = new Set(all.flatMap((d) => [...d.recordClasses.map((c) => c.id),
      ...d.terms.flatMap((section) => section.terms.map((t) => t.id))]));
  }).catch(() => {});
  const plainNames = $derived(namesFor(prefix, singular));
  const qualified = $derived(taken.has(plainNames.recordClass)
    || columns.some((c) => TERM_KINDS.has(c.kind) && taken.has(plainNames.term(c.name))));
  const names = $derived(qualified ? namesFor(prefix, singular, qualifierOf(datasetId)) : plainNames);
  const reused = $derived(columns.filter((c) => c.use));
  const recordClass = $derived(adding ? recordClassId : reused.length ? [termOf(reused[0].use)?.record].flat()[0] : names.recordClass);
  const terms = $derived(columns.filter((c) => !c.use && TERM_KINDS.has(c.kind)));
  const problems = $derived(file ? [
    adding && !draft ? 'Choose the dataset it adds to.' : null,
    !label.trim() ? `Give the ${adding ? 'source' : 'dataset'} a name.` : null,
    !/^[a-z][a-z0-9-]*$/.test(datasetId) ? 'The short name takes lowercase letters, digits and hyphens.' : null,
    periodProblem(period, { timestampColumn, headers: file.parsed.headers }),
    !adding && reused.length && holder.has(recordClass)
      ? `Records of ${recordClass} are already the ${holder.get(recordClass)} dataset's: add this file to it as a source instead.` : null,
    ...columnProblems(columns),
  ].filter(Boolean) : []);

  const reviewed = () => columns.map(({ name, kind, text, shared, range, values, checked, delimiter, edges, shareAs, use }) =>
    ({ name, kind, text, shared, range, values, checked, delimiter, edges, shareAs, use: use ?? null }));
  const sourceOf = (sourceLabel) => ({ id: datasetId, label: sourceLabel, fileName: file.fileName,
    mediaType: file.mediaType, csvText: file.csvText, sourceKey, timestampColumn });
  async function takeIn(sourceId) {
    busy = 'Taking in its rows…';
    await runIngest(sourceId, { onProgress: (job) => {
      if (job.progress?.message) busy = `Taking in its rows: ${job.progress.message}`;
    } });
  }

  async function confirm() {
    if (adding) return confirmSource();
    error = null;
    busy = 'Saving the dataset, its vocabulary and its source…';
    try {
      const result = await createDataset({
        dataset: { id: datasetId, label, singular },
        source: sourceOf(`${label.trim()} (${file.fileName})`),
        period,
        vocabulary: chosen?.choice ?? null,
        columns: reviewed(),
      });
      await takeIn(result.sourceId);
      go('data/dataset', { id: result.dataset.id, tab: 'hood' });
    } catch (e) { error = e.message; busy = null; }
  }

  async function confirmSource() {
    error = null;
    busy = 'Saving the source and its mapping…';
    try {
      const result = await addSource(target, { recordClass: recordClassId, source: sourceOf(label.trim()), period, columns: reviewed() });
      await takeIn(result.sourceId);
      go('data/dataset', { id: result.dataset.id });
    } catch (e) { error = e.message; busy = null; }
  }

  const example = (c) => c.values.slice(0, 4).join(', ') + (c.values.length > 4 ? ', …' : '');
</script>

{#if adding}
<h1 class="wf2-title wf2-title-level-1">Add a source to a dataset</h1>
<p class="wf2-subtitle">Another export that fills a dataset you have: its columns are read as the dataset's terms,
  matched by name. A column with no match is left out, unless you make it a new term.</p>
{:else}
<h1 class="wf2-title wf2-title-level-1">New dataset from a file</h1>
<p class="wf2-subtitle">Every column of the file becomes a term of the dataset: one of a vocabulary you choose, or
  one of your own named after its header. Say what type of value each holds and what may be shared; the file
  becomes the dataset's first source.</p>
{/if}

<Section title="The dataset"><Panel>
  <fieldset class="period">
    <label class="wf2-checkline"><input type="radio" bind:group={mode} value="new" onchange={remake} /> A new dataset, made from this file</label>
    <div class="wf2-row" style="gap:8px; align-items:center; flex-wrap:wrap">
      <label class="wf2-checkline"><input type="radio" bind:group={mode} value="add" onchange={remake} /> Another source for</label>
      <select class="wf2-control" style="width:auto" bind:value={target} onchange={() => { if (mode !== 'add') { mode = 'add'; remake(); } }} aria-label="The dataset it adds to">
        <option value="">Choose a dataset…</option>
        {#each datasets as d (d.id)}<option value={d.id}>{d.label}</option>{/each}
      </select>
    </div>
  </fieldset>
  {#if adding}
    {#if draft?.recordClasses.length > 1}
      <label class="wf2-field">Its rows are
        <select class="wf2-control mono" style="width:auto" value={recordClassId}
          onchange={(e) => { recordClassId = e.currentTarget.value; matchToDataset(); }}>
          {#each draft.recordClasses as r (r.id)}<option value={r.id}>{r.id}</option>{/each}
        </select></label>
    {/if}
    <div class="wf2-row" style="gap:12px; flex-wrap:wrap">
      <label class="wf2-field">Source name <input class="wf2-control" bind:value={label} placeholder="Survey, second round" /></label>
      <label class="wf2-field">Short name
        <input class="wf2-control mono" value={datasetId} placeholder="survey-t1"
          oninput={(e) => { idEdited = true; id = e.currentTarget.value; }} /></label>
    </div>
    {#if draft}
      <p class="wf2-hint">Its records are <span class="mono">{recordClassId}</span>, stored with the rest of {draft.dataset.label}'s.
        {#if terms.length}A column you make a new term gets an id like <span class="mono">{names.term(terms[0].name)}</span>.{/if}</p>
    {/if}
  {:else}
  <div class="wf2-row" style="gap:12px; flex-wrap:wrap">
    <label class="wf2-field">Name <input class="wf2-control" bind:value={label} placeholder="Belonging survey" /></label>
    <label class="wf2-field">Short name
      <input class="wf2-control mono" value={datasetId} placeholder="belonging-survey"
        oninput={(e) => { idEdited = true; id = e.currentTarget.value; }} /></label>
    <label class="wf2-field">Each row is one… <input class="wf2-control" bind:value={singular} placeholder="record" /></label>
  </div>
  <p class="wf2-hint">Its records are <span class="mono">{recordClass}</span>{#if reused.length}, from {chosenName}{:else}, counted as {unitFrom(singular)}{/if};
    {#if terms.length}its own terms get ids like <span class="mono">{names.term(terms[0]?.name ?? 'b1')}</span> in your own vocabulary.{:else}it has no terms of your own.{/if}{#if qualified && terms.length}{' '}Another dataset already uses the names after this file's headers, so these carry its short name.{/if}</p>
  {/if}
</Panel></Section>

<Section title="The file"><Panel>
  <label class="wf2-field">CSV file <input class="wf2-control" type="file" accept=".csv,text/csv" onchange={choose} /></label>
  {#if file}
    <p class="wf2-hint">{file.fileName} · {file.parsed.rows.length} rows · {file.parsed.headers.length} columns. It stays private:
      only the counts you later choose to share leave your Pod.</p>
    <div class="wf2-row" style="gap:12px; flex-wrap:wrap">
      <label class="wf2-field">Record ID
        <select class="wf2-control" bind:value={sourceKey}>
          <option value="@row">Generated row number</option>
          {#each file.parsed.headers as h}<option value={h}>{h}</option>{/each}
        </select></label>
      <label class="wf2-field">Timestamp column
        <select class="wf2-control" bind:value={timestampColumn}>
          <option value="">None</option>
          {#each file.parsed.headers as h}<option value={h}>{h}</option>{/each}
        </select></label>
    </div>
    <fieldset class="period">
      <legend class="wf2-text">What period does a row fall in?</legend>
      <label class="wf2-checkline"><input type="radio" bind:group={period.kind} value="quarter" disabled={!timestampColumn} /> The quarter of its timestamp</label>
      <label class="wf2-checkline"><input type="radio" bind:group={period.kind} value="year" disabled={!timestampColumn} /> The year of its timestamp</label>
      <div class="wf2-row" style="gap:8px; align-items:center">
        <label class="wf2-checkline"><input type="radio" bind:group={period.kind} value="fixed" /> One round for the whole file:</label>
        <input class="wf2-control mono" style="width:6em" bind:value={period.value} aria-label="Round" />
      </div>
      <div class="wf2-row" style="gap:8px; align-items:center">
        <label class="wf2-checkline"><input type="radio" bind:group={period.kind} value="column" /> The round in column</label>
        <select class="wf2-control" style="width:auto" bind:value={period.column} aria-label="Round column">
          <option value="">Choose…</option>
          {#each file.parsed.headers as h}<option value={h}>{h}</option>{/each}
        </select>
      </div>
      <p class="wf2-hint">A survey usually counts rounds (T0, then T1 a year later); an export that grows every
        month counts quarters.</p>
    </fieldset>
  {/if}
</Panel></Section>

{#if file && !adding}
  <Section title="Describe it with"><Panel>
    <label class="wf2-field">Terms
      <select class="wf2-control" value={choiceKey} onchange={(e) => describeWith(e.currentTarget.value)}>
        <option value="">Our own, named after the columns</option>
        {#each choices as c (c.key)}<option value={c.key}>{c.label}</option>{/each}
      </select></label>
    {#if chosen}
      <p class="wf2-hint">{reused.length} of {columns.length} columns match a term of {chosenName} by name. A
        column read as one of its terms takes that term's values and sharing, and its counts are comparable with
        everyone else who uses it. Any other column becomes a term of your own.</p>
    {:else}
      <p class="wf2-hint">Using a vocabulary someone already publishes makes this dataset comparable with theirs
        straight away.</p>
    {/if}
    {#each vocabularies.partners.filter((p) => p.problem) as p}
      <p class="wf2-hint">{p.label}: {p.problem}.</p>
    {/each}
  </Panel></Section>
{/if}
{#if file}
  <Section title="Its columns"><Panel>
    {#if adding}
    <p class="wf2-text">{reused.length} of {columns.length} columns are read as terms of {chosenName}, with the sharing
      they have there{#if terms.length}; {terms.length === 1 ? '1 becomes a new term' : `${terms.length} become new terms`} of it{/if}. The others are left out: give one
      a value type to make it a new term.</p>
    {:else}
    <p class="wf2-text">{#if reused.length}{`${reused.length} columns are read as terms of ${chosenName}; `}{/if}{terms.length} of {columns.length} columns become terms of your own. A term is shared only as
      counts, and only when you say so; free text is never shared.</p>
    {/if}
    <div class="wf2-tablewrap"><table class="wf2-table">
      <thead><tr><th>Column</th>{#if chosen}<th>Term</th>{/if}<th>Value type</th><th>Meaning</th><th>Shared</th><th>Values</th></tr></thead>
      <tbody>
        {#each columns as column, i}
          <tr>
            <td class="mono">{column.name}</td>
            {#if chosen && ['key', 'date'].includes(column.kind)}
              <td>—</td>
            {:else if chosen}
              <td><select class="wf2-control" style="width:auto; max-width:16em" value={column.use ?? ''}
                onchange={(e) => {
                  column.use = e.currentTarget.value || null;
                  if (adding && !column.use) columns[i] = { ...withKind(column, 'skip'), use: null };
                }} aria-label="Term for {column.name}">
                <option value="">{adding ? 'None of its terms' : 'Our own'}</option>
                {#each chosen.vocabulary.terms.filter((t) => t.id === column.use || !columns.some((c) => c.use === t.id)) as t (t.id)}
                  <option value={t.id}>{t.notation ? `${t.notation}: ` : ''}{termText(t).slice(0, 60)}</option>
                {/each}
              </select></td>
            {/if}
            {#if column.use}
              {@const term = termOf(column.use)}
              {@const match = reuseValues(column)}
              <td>{term.unit ?? '—'}</td>
              <td>{termText(term)}<span class="col-stats mono">{term.id}</span></td>
              <td>{term.posture?.treatment === 'drop' ? 'never' : 'as counts'}</td>
              <td>
                {#if term.posture?.treatment === 'drop'}
                  <span class="muted">not read in</span>
                {:else if match?.scheme}
                  <ul class="values">
                    {#each Object.keys(column.raw).filter((v) => v in match.map) as value}
                      <li>{value}{#if conceptLabel(match.scheme, match.map[value]) !== value}{' '}<span class="muted">→ {conceptLabel(match.scheme, match.map[value])}</span>{/if}</li>
                    {/each}
                    {#each match.unmatched as value}
                      <li class="rare">{value} <span class="muted">not in {match.scheme.label ?? match.scheme.id}, left out</span></li>
                    {/each}
                  </ul>
                {:else}
                  <span class="muted">{example(column)}</span>
                {/if}
              </td>
            {:else}
            <td><select class="wf2-control" style="width:auto" value={column.kind}
              onchange={(e) => setKind(i, e.currentTarget.value)}>
              {#each Object.entries(KIND_LABELS) as [kind, text]}<option value={kind}>{text}</option>{/each}
            </select></td>
            <td>
              {#if TERM_KINDS.has(column.kind)}
                <input class="wf2-control" style="min-width:12em" bind:value={column.text} aria-label="Meaning of {column.name}" />
              {:else}
                <span class="muted">{column.kind === 'skip' ? `left out${column.why ? `: ${column.why}` : ''}` : column.kind === 'key' ? 'not a term: tells rows apart' : 'not a term'}</span>
              {/if}
            </td>
            <td>
              {#if column.kind === 'number'}
                <select class="wf2-control" style="width:auto" bind:value={column.shareAs} aria-label="How {column.name} is shared">
                  <option value="bands">in bands</option><option value="total">as a total</option><option value="never">never</option>
                </select>
              {:else if column.kind === 'scale' || LISTED_KINDS.has(column.kind)}
                <label class="wf2-checkline"><input type="checkbox" class="wf2-checkbox" bind:checked={column.shared} /> {column.shared ? 'as counts' : 'never'}</label>
              {:else if column.kind === 'text'}never{:else}—{/if}
            </td>
            <td>
              {#if column.kind === 'scale'}
                <div class="wf2-row" style="gap:6px; align-items:center">
                  <input class="wf2-control" type="number" style="width:4.5em" value={column.range.low}
                    oninput={(e) => (column.range = { ...column.range, low: Number(e.currentTarget.value) })} aria-label="Lowest" />
                  to
                  <input class="wf2-control" type="number" style="width:4.5em" value={column.range.high}
                    oninput={(e) => (column.range = { ...column.range, high: Number(e.currentTarget.value) })} aria-label="Highest" />
                  <span class="muted">seen: {column.values.join(', ')}</span>
                </div>
              {:else if column.kind === 'number'}
                {#if column.shareAs === 'bands'}
                  <label class="wf2-checkline">Band edges
                    <input class="wf2-control" style="width:9em" value={(column.edges ?? []).join(', ')}
                      onchange={(e) => setEdges(column, e.currentTarget.value)} aria-label="Band edges of {column.name}" /></label>
                  <ul class="values">
                    {#each bands(column) as band}
                      <li class:rare={band.count < rareBelow}>{band.label} <span class="muted">{band.count}{band.count < rareBelow ? ', rare' : ''}</span></li>
                    {/each}
                  </ul>
                {:else}
                  <span class="muted">{column.shareAs === 'total' ? 'kept as a number, so a cube can add it up; ' : ''}seen: {column.values[0]} to {column.values.at(-1)}</span>
                {/if}
              {:else if LISTED_KINDS.has(column.kind) && column.shared}
                {#if column.kind === 'list'}
                  <label class="wf2-checkline">Separated by
                    <select class="wf2-control mono" style="width:auto" value={column.delimiter}
                      onchange={(e) => setKind(i, 'list', { delimiter: e.currentTarget.value })}>
                      <option value="|">|</option><option value=";">;</option><option value=",">,</option>
                    </select></label>
                {/if}
                <ul class="values">
                  {#each column.values as value}
                    {@const n = column.counts[value] ?? 0}
                    <li class:rare={n < rareBelow}>{value} <span class="muted">{n}{n < rareBelow ? ', rare' : ''}</span></li>
                  {/each}
                </ul>
                <div class="wf2-row" style="gap:6px; align-items:center; flex-wrap:wrap">
                  <input class="wf2-control" style="width:10em" bind:value={column.added} placeholder="Another option"
                    onkeydown={(e) => { if (e.key === 'Enter') addValue(column); }} />
                  <Button variant="secondary" onclick={() => addValue(column)}>Add</Button>
                </div>
                <label class="wf2-checkline"><input type="checkbox" class="wf2-checkbox" bind:checked={column.checked} />
                  These are all its possible values</label>
              {:else}
                <span class="muted">{example(column)}</span>
              {/if}
            </td>
            {/if}
          </tr>
        {/each}
      </tbody>
    </table></div>
    <p class="wf2-hint">A shared category's or list's values are published with your vocabulary, so check that
      each holds only the values the column may take: a rare free-form value can point to a person. A scale is
      published as its full range, not just the values someone chose. A number is shared in bands (each edge is
      the top of its band), or kept as a number that a cube adds up; a row holding several values of a list
      counts once for each.</p>
  </Panel></Section>

  {#if problems.length}
    <Alert title="Before you can create it" variant="info"><ul>{#each problems as p}<li>{p}</li>{/each}</ul></Alert>
  {/if}
  {#if error}<Alert title={adding ? 'Could not add the source' : 'Could not create the dataset'} variant="danger"><p>{error}</p></Alert>{/if}
  <div class="wf2-row" style="gap:12px; align-items:center">
    <Button variant="primary" onclick={confirm} disabled={Boolean(busy) || problems.length > 0}>{adding ? 'Add the source and take in its rows' : 'Create dataset and take in its rows'}</Button>
    {#if busy}<span class="wf2-hint">{busy}</span>{/if}
  </div>
{/if}

<style>
  .period { border: 0; padding: 0; margin: var(--space-2) 0 0; display: grid; gap: 6px; }
  .values { margin: 0 0 6px; padding-left: 1.2em; }
  .muted { color: var(--muted); }
  .rare { color: var(--wf2-color-warning, #a15c00); }
</style>
